import {
  Container,
  Title,
  Text,
  Stack,
  Modal,
  Badge,
  Image,
  Group,
  Button,
  ActionIcon,
  Tooltip,
  ScrollArea,
  Divider,
} from "@mantine/core";
import { useState, useEffect } from "react";
import { useDisclosure } from "@mantine/hooks";
import { IconLink, IconMapPin } from "@tabler/icons-react";
import {
  useOrganMapTemplatesAdmin,
  useCreateOrganMapTemplate,
  useUpdateOrganMapTemplate,
  useToggleOrganMapTemplateActive,
} from "../../../hooks/admin/useOrganMapTemplates";
import AdminDataTable, {
  AdminColumn,
} from "../../../components/admin/shared/AdminDataTable";
import OrganMapTemplateForm, {
  OrganMapTemplateFormValues,
} from "../../../components/admin/organ-map-templates/OrganMapTemplateForm";
import SourcesModal from "../../../components/admin/organ-map-templates/SourcesModal";
import SubsitePositionsModal from "../../../components/admin/organ-map-templates/SubsitePositionsModal";
import {
  OrganMapTemplate,
  organMapTemplatesService,
} from "../../../services/admin/organMapTemplates.service";

function ResolvedImage({ s3Key, alt }: { s3Key: string; alt: string }) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (s3Key && !s3Key.startsWith("http")) {
      organMapTemplatesService
        .getDownloadUrl(s3Key)
        .then((res) => setUrl(res.url))
        .catch(() => setUrl(null));
    } else {
      setUrl(s3Key);
    }
  }, [s3Key]);

  if (!url) {
    return (
      <Text size="xs" c="dimmed">
        —
      </Text>
    );
  }

  return (
    <Image
      src={url}
      alt={alt}
      w={40}
      h={40}
      fit="contain"
      fallbackSrc="data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40'%3E%3Crect width='40' height='40' fill='%23e9ecef'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23868e96' font-size='10'%3EN/A%3C/text%3E%3C/svg%3E"
    />
  );
}

export default function OrganMapTemplatesAdminPage() {
  const [formOpened, { open: openForm, close: closeForm }] =
    useDisclosure(false);
  const [editingTemplate, setEditingTemplate] =
    useState<OrganMapTemplate | null>(null);
  const [sourcesTemplate, setSourcesTemplate] =
    useState<OrganMapTemplate | null>(null);
  const [positionsTemplate, setPositionsTemplate] =
    useState<OrganMapTemplate | null>(null);

  const { data: templates = [], isLoading } = useOrganMapTemplatesAdmin();
  const createTemplate = useCreateOrganMapTemplate();
  const updateTemplate = useUpdateOrganMapTemplate();
  const toggleActive = useToggleOrganMapTemplateActive();

  const handleAdd = () => {
    setEditingTemplate(null);
    openForm();
  };

  const handleEdit = (item: OrganMapTemplate) => {
    setEditingTemplate(item);
    openForm();
  };

  const handleCloseForm = () => {
    setEditingTemplate(null);
    closeForm();
  };

  const handleOpenSources = (item: OrganMapTemplate) => {
    setSourcesTemplate(item);
  };

  const handleCloseSources = () => {
    setSourcesTemplate(null);
  };

  const handleOpenPositions = (item: OrganMapTemplate) => {
    setPositionsTemplate(item);
  };

  const handleClosePositions = () => {
    setPositionsTemplate(null);
  };

  const handleSubmit = async (values: OrganMapTemplateFormValues) => {
    const payload = {
      ...values,
      width: values.width === "" ? undefined : values.width,
      height: values.height === "" ? undefined : values.height,
      description: values.description || undefined,
      imageUrl: values.imageUrl || undefined,
      svgContent: values.svgContent || undefined,
    };

    if (editingTemplate) {
      await updateTemplate.mutateAsync({ id: editingTemplate.id, ...payload });
    } else {
      await createTemplate.mutateAsync(payload);
    }
    handleCloseForm();
  };

  const handleToggleActive = async (item: OrganMapTemplate) => {
    await toggleActive.mutateAsync({ id: item.id, isActive: !item.isActive });
  };

  const columns: AdminColumn<OrganMapTemplate>[] = [
    {
      key: "name",
      label: "Name",
      sortable: true,
      render: (item) => (
        <Text size="sm" fw={500}>
          {item.name}
        </Text>
      ),
    },
    {
      key: "organName",
      label: "Organ Name",
      sortable: true,
      render: (item) => <Text size="sm">{item.organName}</Text>,
    },
    {
      key: "imageUrl",
      label: "Image",
      width: "80px",
      render: (item) =>
        item.imageUrl ? (
          <ResolvedImage s3Key={item.imageUrl} alt={item.name} />
        ) : (
          <Text size="xs" c="dimmed">
            —
          </Text>
        ),
    },
    {
      key: "linkedSourcesCount",
      label: "Sources",
      width: "100px",
      render: (item) => (
        <Badge color="violet" variant="light" size="sm">
          {item.linkedSourcesCount ?? 0} sources
        </Badge>
      ),
    },
    {
      key: "positionedSubsitesCount",
      label: "Positions",
      width: "110px",
      render: (item) => (
        <Badge color="cyan" variant="light" size="sm">
          {item.positionedSubsitesCount ?? 0} positions
        </Badge>
      ),
    },
    {
      key: "dimensions",
      label: "Dimensions",
      width: "120px",
      render: (item) => (
        <Text size="sm">
          {item.width && item.height ? `${item.width} × ${item.height}` : "—"}
        </Text>
      ),
    },
    {
      key: "isActive",
      label: "Status",
      width: "100px",
      render: (item) => (
        <Badge color={item.isActive ? "green" : "red"} variant="light">
          {item.isActive ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      key: "actions",
      label: "",
      width: "90px",
      render: (item) => (
        <Group gap={4} wrap="nowrap">
          <Tooltip label="Linked Sources">
            <ActionIcon
              variant="subtle"
              color="violet"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenSources(item);
              }}
            >
              <IconLink size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Subsite Positions">
            <ActionIcon
              variant="subtle"
              color="cyan"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenPositions(item);
              }}
            >
              <IconMapPin size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      ),
    },
  ];

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <div>
          <Title order={2}>Organ Map Templates</Title>
          <Text size="sm" c="dimmed" mt="xs">
            Manage visual organ diagrams used in pathology reports. Link sources
            and position subsites on each diagram.
          </Text>
        </div>

        <AdminDataTable
          data={templates}
          columns={columns}
          isLoading={isLoading}
          onAdd={handleAdd}
          onEdit={handleEdit}
          onToggleActive={handleToggleActive}
          getId={(item) => item.id}
          getIsActive={(item) => item.isActive}
          searchPlaceholder="Search organ map templates..."
          emptyMessage="No organ map templates configured"
        />
      </Stack>

      {/* Modal 1: Add / Edit template details */}
      <Modal
        opened={formOpened}
        onClose={handleCloseForm}
        title={
          editingTemplate
            ? `Edit: ${editingTemplate.name}`
            : "Add Organ Map Template"
        }
        size="lg"
        closeOnClickOutside={false}
        styles={{
          body: {
            display: "flex",
            flexDirection: "column",
            padding: 0,
            maxHeight: "calc(100vh - 200px)",
            overflow: "hidden",
          },
        }}
      >
        <ScrollArea style={{ flex: 1, overflow: "auto" }} px="md" pt="md">
          <OrganMapTemplateForm
            formId="organ-map-template-form"
            initialValues={editingTemplate || undefined}
            onSubmit={handleSubmit}
            onCancel={handleCloseForm}
            submitLabel={editingTemplate ? "Update" : "Create"}
            isSubmitting={createTemplate.isPending || updateTemplate.isPending}
          />
        </ScrollArea>
        <Divider />
        <Group justify="space-between" p="md">
          <Button variant="default" onClick={handleCloseForm}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="organ-map-template-form"
            loading={createTemplate.isPending || updateTemplate.isPending}
          >
            {editingTemplate ? "Update" : "Create"}
          </Button>
        </Group>
      </Modal>

      {/* Modal 2: Linked Sources */}
      {sourcesTemplate && (
        <SourcesModal
          opened={!!sourcesTemplate}
          onClose={handleCloseSources}
          template={sourcesTemplate}
        />
      )}

      {/* Modal 3: Subsite Positions */}
      {positionsTemplate && (
        <SubsitePositionsModal
          opened={!!positionsTemplate}
          onClose={handleClosePositions}
          template={positionsTemplate}
        />
      )}
    </Container>
  );
}
